import { CommonModule } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { LucideAngularModule } from 'lucide-angular';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UserService } from '../../core/services/user.service';
import { AuthService } from '../../core/services/auth.service';
import { ErrorDialogComponent } from '../../shared/components/error-dialog/error-dialog';
import { UserSettingsAddressComponent } from './components/user-settings_address/user-settings_address';
import { UserSettingsContactsComponent } from './components/user-settings_contacts/user-settings_contacts';
import { UserSettingsPersonalInfoComponent } from './components/user-settings_personal-info/user-settings_personal-info';
import { environment } from '../../../environments/environment';

@Component({
	selector: 'app-user-settings',
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatCardModule,
		MatSelectModule,
		MatDatepickerModule,
		MatTableModule,
		LucideAngularModule,
		MatButtonModule,
		MatTooltipModule,
		UserSettingsPersonalInfoComponent,
		UserSettingsAddressComponent,
		UserSettingsContactsComponent,
		MatSnackBarModule
	],
	templateUrl: './user-settings.html',
	styleUrl: './user-settings.scss'
})
export class UserSettingsComponent implements OnInit {
	loading = signal(false);
	requestForm = signal(false);

	// Estado de edición por sección
	editandoPersonal = signal(false);
	editandoDomicilios = signal(false);
	editandoContactos = signal(false);

	userData = signal<any | null>(null);
	addressesData = signal<any[]>([]);
	contactsData = signal<any | null>(null);

	modoEdicion = true;

	personalInfoFormData = signal<any | null>(null);
	addressesFormData = signal<any[]>([]);
	contactsFormData = signal<any | null>(null);

	private pendingSubmit = false;
	private pendingAfipUpgrade = false;
	private pendingJwtPayload: any = null;

	get puedeSubirDeNivel(): boolean {
		const nivel = Number(this.userData()?.nivelValidacion);
		return nivel === 0 || nivel === 1;
	}

	get profileInitials(): string {
		const data = this.userData();
		if (!data) return '?';
		const n = (data.nombre || '').charAt(0).toUpperCase();
		const a = (data.apellido || '').charAt(0).toUpperCase();
		return n + a || '?';
	}

	get profileNivel(): string {
		return this.userData()?.nivelValidacion ?? '0';
	}

	constructor(
		private userService: UserService,
		private authService: AuthService,
		private dialog: MatDialog,
		private snackBar: MatSnackBar,
		private router: Router,
		private route: ActivatedRoute
	) {}

	ngOnInit(): void {
		const userinfo = this.route.snapshot.queryParamMap.get('userinfo');
		if (userinfo) {
			this.router.navigate([], { replaceUrl: true });
			const payload = this.parseJwt(userinfo);
			if (!payload) {
				this.dialog.open(ErrorDialogComponent, { width: '400px', data: 'Token de validación inválido.' });
			} else if (Date.now() / 1000 > payload.exp) {
				this.dialog.open(ErrorDialogComponent, { width: '400px', data: 'El token de validación expiró. Por favor, intentá nuevamente.' });
			} else {
				this.pendingJwtPayload = payload;
				this.pendingAfipUpgrade = true;
			}
		}
		this.loadUserData();
	}

	private parseJwt(token: string): any | null {
		const parts = token.split('.');
		if (parts.length !== 3) return null;
		try {
			return JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
		} catch {
			return null;
		}
	}

	loadUserData(): void {
		this.loading.set(true);

		this.userService.getUserInfo().subscribe({
			next: (profile) => {
				if (profile.userInfo) {
					this.userData.set(profile.userInfo);

					this.personalInfoFormData.set({
						tipoDeDocumento: profile.userInfo.tipoDeDocumento ?? '',
						numeroDocumento: profile.userInfo.numeroDocumento ?? '',
						cuil: profile.userInfo.cuil ?? profile.userInfo.username ?? '',
						username: profile.userInfo.username ?? '',
						nombre: profile.userInfo.nombre ?? '',
						apellido: profile.userInfo.apellido ?? '',
						fechaDeNacimiento: profile.userInfo.fechaDeNacimiento ?? null,
						sexo: profile.userInfo.sexo ?? '',
						nacionalidad: profile.userInfo.nacionalidad ?? ''
					});

					this.addressesData.set(profile.userInfo.domicilios || []);
					this.addressesFormData.set(profile.userInfo.domicilios || []);

					this.contactsData.set({
						correoElectronico: profile.userInfo.correoElectronico,
						celular: profile.userInfo.celular,
						telefono: profile.userInfo.telefono,
						telefonoLaboral: profile.userInfo.telefonoLaboral,
						emailValidado: profile.userInfo.emailValidado
					});

					/**
					 * 🔹 Inicializamos también el formData de contactos
					 * para que submit no envíe null si el usuario no tocó nada.
					 */
					this.contactsFormData.set({
						correoElectronico: profile.userInfo.correoElectronico,
						celular: profile.userInfo.celular,
						telefono: profile.userInfo.telefono,
						telefonoLaboral: profile.userInfo.telefonoLaboral,
						emailValidado: profile.userInfo.emailValidado
					});
				}

				this.loading.set(false);
				if (this.pendingAfipUpgrade) {
					this.pendingAfipUpgrade = false;
					this.executeAfipUpgrade();
				}
			},
			error: () => this.loading.set(false)
		});
	}

	submit(): void {
		this.pendingSubmit = true;
		this.requestForm.set(true);
	}

	guardarPersonal(): void {
		this.pendingSubmit = true;
		this.requestForm.set(false);
		setTimeout(() => this.requestForm.set(true));
	}

	cancelarPersonal(): void {
		this.editandoPersonal.set(false);
		this.loadUserData();
	}

	guardarDomicilios(): void {
		this.editandoDomicilios.set(false);
		this.executeUpdate();
	}

	cancelarDomicilios(): void {
		this.editandoDomicilios.set(false);
		this.loadUserData();
	}

	guardarContactos(): void {
		this.editandoContactos.set(false);
		this.executeUpdate();
	}

	cancelarContactos(): void {
		this.editandoContactos.set(false);
		this.loadUserData();
	}

	cerrarSesion(): void {
		this.authService.logout();
	}

	private executeUpdate(): void {
		const id = this.userData()?.id;
		if (!id) return;

		const personalInfo = this.personalInfoFormData();
		const fechaDeNacimiento = (() => {
			const val = personalInfo?.fechaDeNacimiento;
			if (!val) return null;
			if (typeof val === 'string') return val;
			const d = val as Date;
			return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
		})();

		const userToUpdate = {
			...this.userData(),
			...personalInfo,
			fechaDeNacimiento,
			domicilios: this.addressesFormData().map(({ lat, lon, ...rest }: any) => rest),
			...this.contactsFormData()
		};

		this.loading.set(true);
		this.userService.updateVecino(id, userToUpdate).subscribe({
			next: () => {
				this.loading.set(false);
				this.snackBar.open('Cambios guardados correctamente.', 'Cerrar', {
					duration: 4000,
					panelClass: ['snackbar-success']
				});
			},
			error: (err) => {
				this.loading.set(false);
				const msg =
					err?.error?.error ??
					err?.error?.message ??
					'Error al guardar los cambios. Intente nuevamente.';
				this.dialog.open(ErrorDialogComponent, { width: '400px', data: msg });
			}
		});
	}

	cambiarContrasena(): void {
		const id = this.userData()?.id;
		if (!id) return;

		this.loading.set(true);
		this.userService.resetPassword(id).subscribe({
			next: () => {
				this.loading.set(false);
				this.snackBar.open('Se envió correctamente el correo para restablecer la contraseña.', 'Cerrar', {
					duration: 4000,
					panelClass: ['snackbar-success']
				});
			},
			error: (err) => {
				this.loading.set(false);
				const msg =
					err?.error?.error ??
					err?.error?.message ??
					'Error al enviar el correo de restablecimiento de contraseña. Intente nuevamente.';
				this.dialog.open(ErrorDialogComponent, {
					width: '400px',
					data: { title: 'No pudimos restablecer la contraseña', message: msg }
				});
			}
		});
	}

	subirDeNivel(): void {
		const finalRedirect = encodeURIComponent(window.location.origin + '/user/settings');
		const nivel = Number(this.userData()?.nivelValidacion);
		const url = nivel === 0 ? environment.autenticarUrl : environment.afipUrl;
		window.location.href = `${url}?final_redirect=${finalRedirect}`;
	}

	private executeAfipUpgrade(): void {
		const id = this.userData()?.id;
		if (!id) return;

		const nivel = Number(this.userData()?.nivelValidacion);
		const proveedorEsperado = nivel === 0 ? 'renaper' : 'afip';
		const proveedor = this.pendingJwtPayload?.user_info?.proveedor?.toLowerCase();
		this.pendingJwtPayload = null;

		if (proveedor !== proveedorEsperado) {
			this.dialog.open(ErrorDialogComponent, { width: '400px', data: 'El proveedor de validación no es válido.' });
			return;
		}

		const patch = nivel === 0
			? { nivelValidacion: 1, origenValidacion: 'ReNaPer' }
			: { nivelValidacion: 2, origenValidacion: 'AFIP' };

		this.loading.set(true);
		this.userService
			.updateVecino(id, {
				...this.userData(),
				...patch
			})
			.subscribe({
				next: () => {
					this.loading.set(false);
					this.loadUserData();
					this.snackBar.open('¡Nivel actualizado correctamente!', 'Cerrar', {
						duration: 4000,
						panelClass: ['snackbar-success']
					});
				},
				error: (err) => {
					this.loading.set(false);
					const msg =
						err?.error?.error ??
						err?.error?.message ??
						'Error al actualizar el nivel. Intente nuevamente.';
					this.dialog.open(ErrorDialogComponent, { width: '400px', data: msg });
				}
			});
	}

	navigateBack(): void {
		if (!environment.rootURL) {
			this.authService.login();
		} else {
			window.location.href = environment.rootURL;
		}
	}

	onPersonalInfoChange = (data: any) => {
		this.personalInfoFormData.set(data);
		if (this.pendingSubmit) {
			this.pendingSubmit = false;
			this.requestForm.set(false);
			this.editandoPersonal.set(false);
			this.userData.set({ ...this.userData(), ...data });
			this.executeUpdate();
		}
	};
	onAddressesChange = (data: any[]) => this.addressesFormData.set(data);
	onContactsChange = (data: any) => this.contactsFormData.set(data);
}
